import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { useContent } from "../lib/content";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, Phone } from "lucide-react";

export default function CartDrawer() {
  const { open, setOpen, items, removeItem, updateQty } = useCart();
  const { content: c } = useContent();
  const navigate = useNavigate();

  const checkout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-[var(--srishti-bg)] border-[var(--srishti-border)] p-0 flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[var(--srishti-border)]">
          <SheetTitle className="font-serif-display text-2xl text-[var(--srishti-text)]">
            Your Enquiry List
          </SheetTitle>
          <p className="text-xs eyebrow text-[var(--srishti-saffron)]">
            {items.length} {items.length === 1 ? "item" : "items"}
          </p>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="text-center py-16" data-testid="cart-empty">
              <p className="font-serif-display text-2xl text-[var(--srishti-text)] mb-2">
                Your list is empty
              </p>
              <p className="text-sm text-[var(--srishti-text-secondary)] mb-6">
                Add items here to enquire — we&apos;ll share prices &amp; availability when you submit.
              </p>
              <Link to="/shop" onClick={() => setOpen(false)} className="btn-maroon">
                Browse the Shop
              </Link>
            </div>
          ) : (
            <ul className="space-y-5">
              {items.map((it) => (
                <li
                  key={it.product_id}
                  className="flex gap-4 pb-5 border-b border-[var(--srishti-border)] last:border-b-0"
                  data-testid={`cart-item-${it.product_id}`}
                >
                  <img
                    src={it.image}
                    alt={it.name}
                    className="w-20 h-24 object-cover"
                  />
                  <div className="flex-1 flex flex-col">
                    <p className="font-serif-display text-lg leading-tight">{it.name}</p>
                    <p className="text-xs text-[var(--srishti-text-secondary)] mt-1 uppercase tracking-widest">
                      Price on enquiry
                    </p>
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center border border-[var(--srishti-border)]">
                        <button
                          className="p-1.5"
                          onClick={() => updateQty(it.product_id, it.quantity - 1)}
                          aria-label="Decrease"
                          data-testid={`decrease-${it.product_id}`}
                        >
                          <Minus size={14} />
                        </button>
                        <span className="px-3 text-sm">{it.quantity}</span>
                        <button
                          className="p-1.5"
                          onClick={() => updateQty(it.product_id, it.quantity + 1)}
                          aria-label="Increase"
                          data-testid={`increase-${it.product_id}`}
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(it.product_id)}
                        className="text-[var(--srishti-text-secondary)] hover:text-[var(--srishti-maroon)]"
                        aria-label="Remove"
                        data-testid={`remove-${it.product_id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[var(--srishti-border)] px-6 py-5 bg-[var(--srishti-bg-alt)]">
            <a
              href={`tel:${(c.store_phone || "").replace(/\s+/g, "")}`}
              className="flex items-center gap-3 mb-4 text-sm text-[var(--srishti-text)] hover:text-[var(--srishti-maroon)]"
              data-testid="cart-call-link"
            >
              <Phone size={16} className="text-[var(--srishti-saffron)] shrink-0" />
              <span>
                Prefer to call? <span className="font-semibold">{c.store_phone}</span>
              </span>
            </a>
            <button
              className="btn-maroon w-full"
              onClick={checkout}
              data-testid="proceed-to-checkout-button"
            >
              Submit Enquiry
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
